import { computed, ref } from 'vue';
import axios from 'axios';
import { buildMazdaRequestUrl } from '@/api/users';
import router, { clearAuthCache } from '@/router';
import store from '@/store';

function isFormDataPayload(data) {
  return typeof FormData !== 'undefined' && data instanceof FormData;
}

let mazda401Redirecting = false;

function readUsernameFromStorage() {
  const directKeys = ['mazdaOperator', 'operator', 'username', 'userName', 'account'];
  for (const key of directKeys) {
    const value = String(localStorage.getItem(key) || '').trim();
    if (value && value !== 'hahaha') {
      return value;
    }
  }

  const jsonKeys = ['userData', 'userInfo', 'currentUser'];
  for (const key of jsonKeys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const data = JSON.parse(raw);
      const value = pickUsernameFromObject(data);
      if (value) return value;
    } catch (e) {
      // ignore invalid localStorage value
    }
  }

  return '';
}

function pickUsernameFromObject(data) {
  if (!data || typeof data !== 'object') {
    return '';
  }

  const candidates = [
    data.username,
    data.userName,
    data.UserName,
    data.loginName,
    data.LoginName,
    data.name,
    data.nickName,
    data.realName,
    data.account,
    data.user,
  ];

  for (const item of candidates) {
    const value = String(item || '').trim();
    if (value && value !== 'hahaha') {
      return value;
    }
  }

  if (data.userInfo) {
    return pickUsernameFromObject(data.userInfo);
  }
  if (data.data) {
    return pickUsernameFromObject(data.data);
  }

  return '';
}

function getCurrentUsername() {
  const userData = store?.getters?.userData || store?.state?.userData || {};
  const username = pickUsernameFromObject(userData) || readUsernameFromStorage();

  if (username) {
    localStorage.setItem('mazdaOperator', username);
  }

  return username;
}

function hasFormDataField(formData, key) {
  if (!formData || typeof formData.has !== 'function') {
    return false;
  }

  try {
    return formData.has(key);
  } catch (e) {
    return false;
  }
}

function attachMazdaOperator(data) {
  const username = getCurrentUsername();
  if (!username) {
    return data === undefined || data === null ? {} : data;
  }

  // 用户名不走自定义请求头，统一放到请求体里，避免正式环境跨域预检被拦截。
  // JSON 走 operator 字段，FormData 走 operator 表单字段。
  if (isFormDataPayload(data)) {
    if (!hasFormDataField(data, 'operator')) {
      data.append('operator', username);
    }
    return data;
  }

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    if (data.operator || data.username || data.userName || data.account) {
      return data;
    }
    return {
      ...data,
      operator: username,
    };
  }

  return {
    operator: username,
  };
}


export function useMazdaServer() {
  const radio1 = ref('CN Server');

  const serverModeLabel = computed(() => (radio1.value === 'Local Server' ? '本地调试' : '远程正式'));

  const serverEnvDesc = computed(() => {
    if (radio1.value === 'CN Server') {
      return '默认进入 CN 环境，适合日常正式操作。';
    }
    if (radio1.value === 'US Server') {
      return '切到 US 环境后，所有请求会直连 US 服务器。';
    }
    return '本地调试放在最后，仅用于开发联调，不参与正式环境操作。';
  });

  const serverTagType = computed(() => {
    if (radio1.value === 'CN Server') return 'success';
    if (radio1.value === 'US Server') return 'warning';
    return 'info';
  });

  const serverToneClass = computed(() => {
    if (radio1.value === 'CN Server') return 'server-tone-cn';
    if (radio1.value === 'US Server') return 'server-tone-us';
    return 'server-tone-local';
  });

  const getMazdaApiUrl = (endpoint) => buildMazdaRequestUrl(radio1.value, endpoint);

  const buildMazdaConfig = (data, config = {}) => {
    const token = localStorage.getItem('authToken');
    const headers = {
      Accept: 'application/json, text/plain, */*',
      ...(config.headers || {}),
    };

    if (token && !headers.Authorization) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (!isFormDataPayload(data) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json;charset=UTF-8';
    }

    return {
      timeout: 30000,
      ...config,
      headers,
    };
  };

  const handleMazdaRequestError = async (error) => {
    const status = error?.response?.status || error?.status;

    if (status === 401) {
      localStorage.removeItem('authToken');
      clearAuthCache();

      if (!mazda401Redirecting) {
        mazda401Redirecting = true;
        try {
          if (router.currentRoute.value.name !== 'LoginView') {
            await router.replace({ name: 'LoginView' });
          }
        } finally {
          setTimeout(() => {
            mazda401Redirecting = false;
          }, 500);
        }
      }
    }

    throw error;
  };

  const mazdaPost = async (endpoint, data = {}, config = {}) => {
    try {
      const requestData = attachMazdaOperator(data);
      return await axios.post(
        getMazdaApiUrl(endpoint),
        requestData,
        buildMazdaConfig(requestData, config)
      );
    } catch (error) {
      await handleMazdaRequestError(error);
    }
  };

  return {
    radio1,
    serverModeLabel,
    serverEnvDesc,
    serverTagType,
    serverToneClass,
    getMazdaApiUrl,
    mazdaPost,
  };
}
